import { useCallback, useEffect, useState } from 'react';
import { getSupabaseClient } from '../supabase-client';
import { reportError } from '../../lib/monitoring';
import type { CapturePhotoRow } from '../database.types';

/**
 * A Capture's ACTIVE photos, in order. M5 / S05-04.
 *
 * AUTHORIZATION IS RLS, NOT A CLIENT CHECK. `capture_photo_select_own` only
 * matches rows the caller owns, so another user's photos return no rows —
 * the same rule `use-capture.ts` follows, and the id in the URL grants nothing.
 *
 * **`removed_at is null` is the product's definition of a live photo (PT §9).**
 * Removal is non-destructive: the row and its Storage objects survive, and the
 * Work Log keeps the event with its actor and time. This filter is what makes
 * a removed photo disappear from the active Capture without anything being
 * deleted, so it is applied HERE, once, rather than left to each screen.
 *
 * Ordered by `position`, which the server allocates at attach time. Photos are
 * not re-ordered by the client and there is no reorder feature in v1.
 */

export type CapturePhotosStatus = 'loading' | 'ready' | 'failed';

export interface CapturePhotosState {
  status: CapturePhotosStatus;
  /** Active rows only — never the removed ones. */
  photos: CapturePhotoRow[];
  error: string | null;
  reload: () => Promise<void>;
}

export function useCapturePhotos(captureId: string | undefined): CapturePhotosState {
  const client = getSupabaseClient();
  const [status, setStatus] = useState<CapturePhotosStatus>(client ? 'loading' : 'failed');
  const [photos, setPhotos] = useState<CapturePhotoRow[]>([]);
  const [error, setError] = useState<string | null>(
    client ? null : 'Crewlink is not connected to a server yet.',
  );

  const fetchPhotos = useCallback(async (): Promise<
    { kind: 'ready'; rows: CapturePhotoRow[] } | { kind: 'failed' }
  > => {
    if (!client || !captureId) return { kind: 'ready', rows: [] };

    const { data, error: queryError } = await client
      .from('capture_photo')
      .select('*')
      .eq('capture_id', captureId)
      .is('removed_at', null)
      .order('position', { ascending: true });

    if (queryError) {
      reportError(queryError, 'capturePhotos.load');
      return { kind: 'failed' };
    }
    return { kind: 'ready', rows: data ?? [] };
  }, [client, captureId]);

  const apply = useCallback(
    (result: { kind: 'ready'; rows: CapturePhotoRow[] } | { kind: 'failed' }) => {
      if (result.kind === 'failed') {
        // The photos failing to load must not take the Capture down with it —
        // the transcript above is still readable, and saying so beats a blank
        // screen.
        setError('Could not load this Capture’s photos. Check your connection and try again.');
        setStatus('failed');
        return;
      }
      setPhotos(result.rows);
      setError(null);
      setStatus('ready');
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void fetchPhotos().then((result) => {
      if (!cancelled) apply(result);
    });
    return () => {
      cancelled = true;
    };
  }, [fetchPhotos, apply]);

  const reload = useCallback(async () => {
    apply(await fetchPhotos());
  }, [fetchPhotos, apply]);

  return { status, photos, error, reload };
}
